import { useField } from 'formik';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getCurrencyFormat } from '../functions/stringFunctions';
import { phoneNumberWithCountryCode } from '../utils/phone-format';

const InputField = ({
	label,
	name,
	placeholder,
	isRequired = true,
	type = 'text',
	labelClassName,
	disabled,
	min,
	max,
	maxLength,
	suffix,
	currency = false,
	isNullable = false,
	phoneNumber = false,
	OnChange,
	...props
}: {
	label: string;
	name: string;
	type?: string;
	disabled?: boolean;
	labelClassName?: string;
	placeholder?: string;
	isRequired?: boolean;
	min?: number;
	max?: number;
	suffix?: string;
	maxLength?: number;
	currency?: boolean;
	isNullable?: boolean;
	phoneNumber?: boolean;
	OnChange?: (val: string | undefined) => void;
}) => {
	const [field, meta, helpers] = useField(name);
	const inputRef = useRef<HTMLInputElement>(null);

	const [cursorPosition, setCursorPosition] = useState(0);
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleChange = (e: any) => {
		let value = e.target.value;
		const currentPosition = inputRef.current?.selectionStart;
		if (type === 'text') {
			value = value.replace(/^\s+/, '').replace(/\s+/g, ' ');
		}
		if (type === 'number') {
			if (value === '' && isNullable) {
				value = '';
			} else {
				value = value.replace(/^0+/, '');
				const updatedValue = getMinOrMaxValue(
					value,
					min,
					max,
				) as number;
				value = Math.floor(updatedValue).toString();
			}
		}
		if (phoneNumber) {
			value = phoneNumberWithCountryCode(value);
		}
		if (currency) {
			const updatedValue = value?.replace(/[^0-9]/g, '');
			if (updatedValue === '' && isNullable) {
				value = null;
			} else {
				value = getMinOrMaxValue(updatedValue, min, max);
				value = getCurrencyFormat(value);
				const updatedCursorPos = getCursorPosition(
					e.target.value,
					value,
					currentPosition as number,
				);
				setCursorPosition(updatedCursorPos);
			}
		}
		helpers.setValue(value);
		OnChange?.(value);
	};

	const getMinOrMaxValue = (value: number, min?: number, max?: number) => {
		let updatedValue: number | null = value;
		if (min && Math.floor(value) < min) {
			updatedValue = min || null;
		}
		if (max && Math.floor(value) > max) {
			updatedValue = max || null;
		}
		return updatedValue;
	};

	const getCursorPosition = (
		currentValue: string,
		formattedValue: string,
		currentCursorPos: number,
	) => {
		if (formattedValue.length === currentValue.length) {
			return currentCursorPos;
		}
		return formattedValue.length > currentValue.length
			? currentCursorPos + 1
			: currentCursorPos - 1;
	};

	useEffect(() => {
		if (currency && inputRef.current) {
			inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
		}
	}, [cursorPosition, inputRef.current]);

	const getValue = useCallback(
		() =>
			disabled &&
			(field?.value === undefined ||
				field?.value === null ||
				field?.value === '')
				? 'NA'
				: field?.value
					? field?.value
					: '',
		[disabled, field],
	);

	return (
		<label
			className={`flex flex-col flex-1 min-w-[100px] max-w-[600px]${labelClassName ?? ''}`}
		>
			<p className="font-semibold text-base text-rcrm-title">
				{label}
				{isRequired ? '*' : ''}
				{<small>{' '.concat(suffix ?? '')}</small>}
			</p>
			<input
				ref={inputRef}
				className={`p-3 rounded-lg text-sm font-medium outline-none border-2
				 ${meta.touched && meta.error ? 'border-rcrm-error' : 'border-rcrm-primary-border-blue'}`}
				type={type}
				{...field}
				value={getValue()}
				placeholder={disabled ? 'NA' : placeholder}
				{...props}
				min={min}
				max={max}
				maxLength={maxLength}
				disabled={disabled}
				onChange={handleChange}
			/>
			{meta.touched && meta.error ? (
				<div className="text-xs text-rcrm-error">{meta.error}</div>
			) : null}
		</label>
	);
};

export default InputField;
